import React, { useState, useEffect } from 'react';
import { usePWA } from '@/hooks/usePWA';
import { FaDownload, FaTimes, FaWifi } from 'react-icons/fa';

const PWAInstallBanner: React.FC = () => {
  const { isOnline, isInstallable, isInstalled, isStandalone, installPWA } = usePWA();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show banner if app is installable and not already installed/standalone
    if (isInstallable && !isInstalled && !isStandalone && !dismissed) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [isInstallable, isInstalled, isStandalone, dismissed]);

  const handleInstall = async () => {
    try {
      await installPWA();
      setShowBanner(false);
    } catch (error) {
      console.error('Installation failed:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    // Remember dismissal for this session
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Check if banner was already dismissed this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  if (!showBanner) {
    return (
      <>
        {/* Offline indicator */}
        {!isOnline && (
          <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-yellow-900 text-center py-2 text-sm font-medium z-50">
            <FaWifi className="inline w-4 h-4 mr-2" />
            You&apos;re offline. Some features may be limited.
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-yellow-900 text-center py-2 text-sm font-medium z-50">
          <FaWifi className="inline w-4 h-4 mr-2" />
          You&apos;re offline. Some features may be limited.
        </div>
      )}

      {/* Install banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              Install DataMapper
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Install the app for faster access, offline support, and a native experience.
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleInstall}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
              >
                <FaDownload className="w-3 h-3" />
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};

export default PWAInstallBanner;